import React, { useState, useEffect, useRef, memo } from 'react';
import { useTranslation, type Language } from '../hooks/useTranslation';
import { MessageContent } from './MessageContent';
import type { ButtonVariant } from '../types/theme';

/**
 * Props for the WelcomeMessage component
 */
interface WelcomeMessageProps {
  /** Agent name for display */
  agentName: string;
  /** Customer name for personalization */
  customerName?: string;
  /** Language for the interface */
  language?: Language;
  /** Callback when the message appears */
  onMessageAppear?: () => void;
  /** Delay before showing the message (in ms) */
  delay?: number;
  /** Whether to skip the typing animation (for already shown welcomes) */
  skipAnimation?: boolean;
  /** Custom welcome text template with placeholders {{customerName}} and {{agentName}} */
  customWelcomeText?: string;
  /** Optional callback when a button in the welcome message is clicked */
  onButtonClick?: (url: string, label: string, variant?: ButtonVariant) => void;
}

/**
 * Reusable welcome message component that simulates a typing animation
 * and displays as a proper chat bubble
 */
const WelcomeMessageComponent: React.FC<WelcomeMessageProps> = ({
  agentName,
  customerName,
  language = 'en',
  onMessageAppear,
  delay = 1000,
  skipAnimation = false,
  customWelcomeText,
  onButtonClick,
}) => {
  const [isTyping, setIsTyping] = useState<boolean>(!skipAnimation);
  const [showMessage, setShowMessage] = useState<boolean>(skipAnimation);
  const hasCalledAppear = useRef<boolean>(false);
  
  // Capture timestamp once on mount to prevent updates on re-renders
  const initialTimestamp = useRef<string>(
    new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })
  );

  const { t } = useTranslation(language);

  // Reset tracking when skipAnimation changes (indicates new welcome context)
  useEffect(() => {
    hasCalledAppear.current = false;
  }, [skipAnimation]);

  useEffect(() => {
    if (hasCalledAppear.current) {
      return; // Already called, don't call again
    }

    if (skipAnimation) {
      // If skipping animation, immediately call onMessageAppear
      hasCalledAppear.current = true;
      onMessageAppear?.();
    } else {
      // Show typing indicator first
      const typingTimer = setTimeout(() => {
        setIsTyping(false);
        setShowMessage(true);
        hasCalledAppear.current = true;
        onMessageAppear?.();
      }, delay);

      return () => clearTimeout(typingTimer);
    }
  }, [delay, onMessageAppear, skipAnimation]);

  /**
   * Generates the welcome text, replacing placeholders in custom template or using default
   */
  const getWelcomeText = (): string => {
    // If custom welcome text is provided, replace placeholders
    if (customWelcomeText) {
      return customWelcomeText
        .replace(/\{\{customerName\}\}/g, customerName || '')
        .replace(/\{\{agentName\}\}/g, agentName);
    }
    
    // Otherwise use default behavior
    return customerName
      ? `Hej ${customerName} - velkommen til ${agentName}!\n\n${t('welcome.subtitle')}`
      : `${t('welcome.title', { agentName })}!\n\n${t('welcome.subtitle')}`;
  };

  const welcomeText = getWelcomeText();

  if (isTyping) {
    // Typing indicator that looks like a regular message
    return (
      <div className="chatbot-welcome-message flex space-x-3 animate-in slide-in-from-left-5 duration-300">
        <div className="chatbot-welcome-avatar w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-bold text-sm">F</span>
        </div>

        <div className="flex-1 max-w-[85%]">
          <div className="chatbot-welcome-bubble bg-card border-[0.5px] border-border/50 rounded-lg rounded-tl-md p-4 shadow-sm">
            {/* Message Header */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">
                {agentName}
              </span>
              <span className="text-xs font-medium text-muted-foreground/70">
                {initialTimestamp.current}
              </span>
            </div>

            {/* Typing Animation */}
            <div className="chatbot-typing-indicator flex items-center space-x-3">
              <div className="flex space-x-1">
                <div className="chatbot-typing-dot w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="chatbot-typing-dot w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="chatbot-typing-dot w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="text-muted-foreground font-medium text-sm">
                {t('status.typing')}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showMessage) {
    // Actual welcome message that looks like a regular message
    return (
      <div className="chatbot-welcome-message flex space-x-3 animate-in slide-in-from-left-5 duration-300">
        <div className="chatbot-welcome-avatar w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-bold text-sm">F</span>
        </div>

        <div className="flex-1 max-w-[85%]">
          <div className="chatbot-welcome-bubble bg-card border-[0.5px] border-border/50 rounded-lg rounded-tl-md p-4 shadow-sm">
            {/* Message Header */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">
                {agentName}
              </span>
              <span className="text-xs font-medium text-muted-foreground/70">
                {initialTimestamp.current}
              </span>
            </div>

            {/* Message Content */}
            <div className="chatbot-welcome-text leading-relaxed text-card-foreground">
              <MessageContent
                content={welcomeText}
                onButtonClick={onButtonClick}
                isTyping={false}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export const WelcomeMessage = memo(WelcomeMessageComponent);
WelcomeMessage.displayName = 'WelcomeMessage';
